import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from "react-router";
import { useDispatch } from 'react-redux';
import { DetailsList, SelectionMode, Callout, TooltipHost, Spinner, SpinnerSize, IColumn, IDetailsRowProps, IRenderFunction } from '@fluentui/react';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react';
import { DependencyAddIcon, SignOutIcon, ShoppingCartIcon } from '@fluentui/react-icons-mdl2';
import IroniaPoints from './IroniaPoints';
import Moment from 'moment';
import { currencyFormatter, percentFormatter, decimalFormatter, longDecimalFormatter, setCurrency } from '@/utils/numberFormatter';
import 'office-ui-fabric-react/dist/css/fabric.css';
import Auth from "../../../../Auth/Auth";
import { parseNumber } from '@/utils/toFixedDecimals';
import { ReactComponent as IncrementIcon } from '@/images/increment.svg';
import { ReactComponent as DecrementIcon } from '@/images/decrement.svg';
import AppendCartLine from '@/utils/AppendCartLine';
import { IInitialRegisters, TCategories, TLevel2PositionRaw, TSubcategories, TSubCategoriesPositions, TAggregation, TAggregationLevel2, TDocuments, TRtbState, IPortfolioTableProps } from '../types/PortfolioTypes';
import { IInfoFundPortfolio } from '@/services/FundService';
import ThreeWayToggle from '@/components/ThreeWayToggle/ThreeWayToggle';

const documentsColumns: IColumn[] =
[
    { key: 'name', name: 'Fichero', fieldName: 'name', minWidth: 200, isResizable: true },
    { key: 'link', name: 'Acción', fieldName: 'url', minWidth: 50, maxWidth: 50, isResizable: false },
];

const dateFormat = (stringDate: string | undefined): string =>
{
    return Moment(stringDate).format('DD/MM/YYYY');
};

const STYLES_CENTER = {cellTitle: {justifyContent: 'center' as const, display: 'flex' as const}};
const INITIAL_RTB_STATE: TRtbState = {key: "linearReturn1d", name: "Rtb 1D"};

const PortfolioTable = (props: IPortfolioTableProps) =>
{
    const [rtbState, setRtbState] = useState<TRtbState>(INITIAL_RTB_STATE);
    const {isResumeWallet, onCollectedItemsUpdate} = props;
    const [registers, setRegisters] = useState<IInitialRegisters[]>([]);
    const [isAttachmentsVisible, setIsAttachmentsVisible] = useState(false);
    const [selectedRegister, setSelectedRegister] = useState<IInitialRegisters | undefined>(undefined);
    const isActiveManagementPlan: boolean = Auth.isActiveManagementPlan();
    const [collectedItems, setCollectedItems] = useState<IInitialRegisters[]>([]);
    const history = useHistory();
    const dispatch = useDispatch();
    const [sending, setSending] = useState("");

    const handleClick = useCallback(async (item: IInitialRegisters) =>
    {
        setSending(item.isin ?? '');
        await AppendCartLine(dispatch, history, {fund: item, amount: 0});
        setSending('');
    }, [dispatch, history]);

    const onChangeRtb = useCallback((toggleValue: boolean | undefined) =>
    {
        setRtbState(
        {
            key: toggleValue ? "linearReturn1d" : "linearReturnYtd",
            name: toggleValue ? "Rtb 1D" : "Rtb YTD",
        });
    }, []);

    const columns = useMemo(() =>
    {
        if(props.isOutdatedPlan === true)
        {
            return[
                { key: 'name', name: '', isCollapsible: false, fieldName: 'name', minWidth: 320, maxWidth: 600, isResizable: true },
                ...(props.showDocuments ? [{ key: 'documents', name: 'Documentos', minWidth: 100, maxWidth: 100, isResizable: false, styles: STYLES_CENTER }] : []),
                { key: 'titles', name: 'Títulos', fieldName: 'titles', minWidth: 100, maxWidth: 100, isResizable: false, styles: STYLES_CENTER },
                { key: 'bought', name: 'Comprado', fieldName: 'bought', minWidth: 110, maxWidth: 110, isResizable: false, styles: STYLES_CENTER },
                { key: 'valuation', name: 'Valoración', fieldName: 'valuation', minWidth: 110, maxWidth: 110, isResizable: false, styles: STYLES_CENTER },
                { key: 'revaluation', name: 'Revalorización', fieldName: 'revaluation', minWidth: 130, maxWidth: 130, isResizable: false, styles: STYLES_CENTER },
                ...(props.showActions ?
                    [{
                        key: 'actions', name: 'Acciones', fieldName: 'actions', minWidth: 90, maxWidth: 90, isResizable: false, styles: STYLES_CENTER,
                        onRender: (item: IInitialRegisters) =>
                        {
                            if (!item?.isin || isResumeWallet === true)
                            {
                                return (<></>);
                            }
                            return(
                                <div style={{display: 'flex', justifyContent: 'center'}}>
                                    <TooltipHost content="Reembolso" id={`refundBtn_${item.isin}`} styles={{root: {display: 'inline-block', paddingLeft: '2px'}}} calloutProps={{gapSpace: 0}}>
                                        <Link to={{pathname: '/refunds', state: {origin: item}}}>
                                            <PrimaryButton className="light-circled-icon" id={`refundBtn_${item.isin}`}>
                                                <SignOutIcon className="ironia-table-icon" />
                                            </PrimaryButton>
                                        </Link>
                                    </TooltipHost>
                                </div>
                            );
                        }
                    }] : [])
            ];
        }

        return[
        { key: 'name', name: '', isCollapsible: false, fieldName: 'name', minWidth: 150, maxWidth: 350, isResizable: true },
        ...(props.showDocuments ? [{ key: 'documents', name: 'Documentos', minWidth: 80, maxWidth: 80, isResizable: false, styles: STYLES_CENTER }] : []),
        { key: 'titles', name: 'Títulos', fieldName: 'titles', minWidth: 70, maxWidth: 70, isResizable: false, styles: STYLES_CENTER },
        { key: 'bought', name: 'Comprado', fieldName: 'bought', minWidth: 70, maxWidth: 70, isResizable: false, styles: STYLES_CENTER },
        { key: 'valuation', name: 'Valoración', fieldName: 'valuation', minWidth: 70, maxWidth: 70, isResizable: false, styles: STYLES_CENTER },
        { key: 'revaluation', name: 'Revalorización', fieldName: 'revaluation', minWidth: 100, maxWidth: 100, isResizable: false, styles: STYLES_CENTER },
        { key: 'revaluationPercentage', name: '% Rev.', fieldName: 'revaluationPercentage', minWidth: 50, maxWidth: 50, isResizable: false, styles: STYLES_CENTER },
        {
            key: rtbState.key, name: rtbState.name,
            fieldName: rtbState.key, minWidth: 120, maxWidth: 160, isResizable: false, styles:
            {
                cellTitle:
                {
                    justifyContent: 'center' as const, display: 'flex' as const,
                    color: "white",
                    width: "140px"
                }
            },
            onRenderHeader: () =>
            (
                <div className="rentabilidadToggle">
                    <span>Rtb</span>
                    <ThreeWayToggle onChange={onChangeRtb} toggleInitialState={rtbState.key === "linearReturn1d" ? 1 : 0}
                        disableMiddlePosition
                        toggleTexts={
                        {
                            turnOnText: "1D",
                            turnOffText: "YTD"
                        }}
                    />
                </div>
            ),
        },
        { key: 'volatilityOneYear', name: 'Vola 1Y', fieldName: 'volatilityOneYear', minWidth: 50, maxWidth: 50, isResizable: false, styles: STYLES_CENTER },
        { key: 'weight', name: 'Peso', fieldName: 'weight', minWidth: 50, maxWidth: 50, isResizable: false, styles: STYLES_CENTER },
        { key: 'pmpc', name: 'PMPC', fieldName: 'pmpc', minWidth: 50, maxWidth: 70, isResizable: true, styles: STYLES_CENTER },
        { key: 'vLiquid', name: 'V. liq', fieldName: 'vLiquid', minWidth: 50, maxWidth: 150, isResizable: true, styles: STYLES_CENTER },
        { key: 'exchangeRate', name: 'TC', fieldName: 'exchangeRate', minWidth: 50, maxWidth: 50, isResizable: false, styles: STYLES_CENTER },
        { key: 'liquidDate', name: 'Fecha VL', fieldName: 'liquidDate', minWidth: 80, maxWidth: 80, isResizable: false, styles: STYLES_CENTER },
        ...(props.showActions ?
            [{
                key: 'actions', name: 'Acciones', fieldName: 'actions', minWidth: 110, maxWidth: 110, isResizable: false, styles: STYLES_CENTER,
                onRender: (item: IInitialRegisters) =>
                {
                    if(!item?.isin || isResumeWallet === true)
                    {
                        return (<></>);
                    }
                    if(props.isOutdatedPlan === true)
                    {
                        return(
                            <TooltipHost content="Reembolso" id={`refundBtn_${item.isin}`} styles={{root: {display: 'inline-block', paddingLeft: '2px'}}} calloutProps={{gapSpace: 0}}>
                                <Link to={{pathname: '/refunds', state: {origin: item}}}>
                                    <PrimaryButton className="light-circled-icon" id={`refundBtn_${item.isin}`}>
                                        <SignOutIcon className="ironia-table-icon" />
                                    </PrimaryButton>
                                </Link>
                            </TooltipHost>
                        );
                    }
                    if(props.activePlan?.outdatedContract)
                    {
                        return (<></>);
                    }
                    return(
                        <React.Fragment>
                            {isActiveManagementPlan === true &&
                            (
                                <React.Fragment>
                                    <TooltipHost content="Reembolso" id={`refundBtn_${item.isin}`} styles={{root: {display: 'inline-block', paddingLeft: '2px'}}} calloutProps={{gapSpace: 0}}>
                                        <Link to={{ pathname: '/refunds', state: {origin: item} }}>
                                            <PrimaryButton className="light-circled-icon" id={`refundBtn_${item.isin}`}>
                                                <SignOutIcon className="ironia-table-icon" />
                                            </PrimaryButton>
                                        </Link>
                                    </TooltipHost>
                                </React.Fragment>
                            )
                            }
                            {!isActiveManagementPlan &&
                            (
                                <React.Fragment>
                                    {item.available === true && (<TooltipHost content="Carrito" id={`cartBtn_${item.isin}`} styles={{root: {display: 'inline-block', paddingLeft: '2px'}}} calloutProps={{gapSpace: 0}}>
                                        <PrimaryButton className="light-circled-icon"
                                            id={`cartBtn_${item.isin}`}
                                            onClick={() => handleClick(item)}
                                            disabled={sending === item.isin}
                                        >
                                            {sending === item.isin && <Spinner size={SpinnerSize.small} className="button-spinner" />}
                                            {sending !== item.isin && <ShoppingCartIcon className="ironia-table-icon" shopping-cart />}
                                        </PrimaryButton>
                                    </TooltipHost>)}
                                    {props.funds && props.funds?.find(x => x.isin === item.isin)?.switchable === true && (<TooltipHost content="Traspasar" id={`transferBtn_${item.isin}`} styles={{root: {display: 'inline-block', paddingLeft: '2px'}}} calloutProps={{gapSpace: 0}}>
                                        <Link to={{pathname: '/internal-switch', state: {origin: item}}}>
                                            <PrimaryButton className="light-circled-icon" id={`transferBtn_${item.isin}`}>
                                                <DependencyAddIcon className="ironia-table-icon" />
                                            </PrimaryButton>
                                        </Link>
                                    </TooltipHost>)}
                                    <TooltipHost content="Reembolso" id={`refundBtn_${item.isin}`} styles={{root: {display: 'inline-block', paddingLeft: '2px'}}} calloutProps={{gapSpace: 0}}>
                                        <Link to={{pathname: '/refunds', state: {origin: item}}}>
                                            <PrimaryButton className="light-circled-icon" id={`refundBtn_${item.isin}`}>
                                                <SignOutIcon className="ironia-table-icon" />
                                            </PrimaryButton>
                                        </Link>
                                    </TooltipHost>
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    );
                }
            }] : []),
        ...(props.showIroniaPoints ? [{ key: 'ironiaPoints', name: 'Puntos IronIA', fieldName: 'score', minWidth: 140, maxWidth: 200, isResizable: false }] : [])
    ];
    }, [props.showDocuments, props.showActions, props.showIroniaPoints, props.isOutdatedPlan, props.funds, props.activePlan?.outdatedContract, isResumeWallet, isActiveManagementPlan, rtbState, sending, handleClick, onChangeRtb]);

    useEffect(() =>
    {
        const aggregationList = props.reporting?.positions.positionsTo.aggregation ?? [];
        const valuation = aggregationList.reduce((total: number, aggregation: TAggregation) =>
        {
            return total + aggregation.total.value;
        }, 0);

        const plusMinus = aggregationList.reduce((total: number, aggregation: TAggregation) =>
        {
            return total + aggregation.total.plusMinus;
        }, 0);

        const buyAmount = valuation - plusMinus;

        const growth = buyAmount === 0 ? 0 : (valuation - buyAmount) / buyAmount;

        const weight = aggregationList.reduce((total: number, aggregation: TAggregation) =>
        {
            return total + aggregation.total.weight;
        }, 0);

        const categories: TCategories[] = [];

        aggregationList.forEach((aggregation: TAggregation) =>
        {
            const subcategories: TSubcategories[] = [];
            aggregation.level2.forEach((level2: TAggregationLevel2) =>
            {
                const subCategoryPositions: TSubCategoriesPositions[] = [];
                level2.positions.forEach((position: TLevel2PositionRaw) =>
                {
                    const er = position.exchangeRate;
                    const exchangeRateValue = (er != null && er.value !== undefined) ? er.value : 1;
                    const exchangeRateFrom = (er != null && er.from !== undefined) ? er.from : "";

                    subCategoryPositions.push(
                    {
                        name: position.productName,
                        shortName: position.productShortName,
                        instrumentId: position.instrumentId,
                        isin: position.isin,
                        weight: position.weight,
                        titles: position.titles,
                        price: position.lastPriceInProductCurrency,
                        plusMinus: position.plusMinus,
                        plusMinusPercentage: position.plusMinusPercentage,
                        value: position.value,
                        instrumentType: position.instrumentType,
                        valuation: position.value,
                        buyAmount: position.value === 0 || position.value == null ? 0 : position.value - position.plusMinus,
                        exchangeRate: exchangeRateValue,
                        from: exchangeRateFrom,
                        lastPriceDate: position.dateLastPriceAsString,
                        currency: position.currency
                    });
                });

                const level2PlusMinus: number = level2.total.plusMinus;
                const level2PlusMinusPercentage: number = level2.total.plusMinusPercentage;
                const level2BuyAmount: number = level2.total.value === 0 || level2.total.value === null ? 0 : level2.total.value - level2.total.plusMinus;
                const level2Valuation: number = level2.total.value;
                const level2Growth: number = level2BuyAmount === 0 ? 0 : (level2Valuation - level2BuyAmount) / level2BuyAmount;
                const level2Weight: number = level2.total.weight;

                subcategories.push(
                {
                    name: level2.data.name,
                    positions: subCategoryPositions,
                    buyAmount: level2BuyAmount,
                    valuation: level2Valuation,
                    plusMinus: level2PlusMinus,
                    plusMinusPercentage: level2PlusMinusPercentage,
                    growth: level2Growth,
                    weight: level2Weight
                });
            });

            const aggregationBuyAmount: number = aggregation.total.value === 0 || aggregation.total.value === null ? 0 : aggregation.total.value - aggregation.total.plusMinus;
            const aggregationValuation: number = aggregation.total.value;
            const aggregationWeight: number = aggregation.total.weight;
            const aggregationPlusMinus: number = aggregation.total.plusMinus;
            const aggregationPlusMinusPercentage: number = aggregation.total.plusMinusPercentage;
            const aggregationGrowth: number = aggregationBuyAmount === 0 ? 0 : (aggregationValuation - aggregationBuyAmount) / aggregationBuyAmount;

            const categoriesPosition: TSubCategoriesPositions[] = [];
            if(aggregation.level2.length === 0)
            {
                aggregation.positions.forEach((position: TLevel2PositionRaw) =>
                {
                    if(position.instrumentType !== 21)
                    {
                        var date = new Date(position.date);
                        var dateAsString = String(date.getDate()).padStart(2, '0') + '/' + String(date.getMonth() + 1).padStart(2, '0') + '/' + date.getFullYear();

                        categoriesPosition.push(
                        {
                            name: "Operación pendiente (EUR)",
                            shortName: position.productShortName,
                            instrumentId: position.instrumentId,
                            isin: position.isin !== null ? position.isin : '',
                            weight: position.weight,
                            titles: position.titles,
                            price: undefined,
                            plusMinus: currencyFormatter.format(position.plusMinus),
                            plusMinusPercentage: position.plusMinusPercentage,
                            value: currencyFormatter.format(position.value),
                            instrumentType: position.instrumentType,
                            valuation: currencyFormatter.format(position.value === 0 || position.value === null ? 0 : position.value - position.plusMinus),
                            exchangeRate: null,
                            from: null,
                            lastPriceDate: dateAsString,
                            currency: position.currency,
                        });
                    }
                    else
                    {
                        categoriesPosition.push(
                        {
                            name: position.productName,
                            shortName: position.productShortName,
                            instrumentId: position.instrumentId,
                            isin: position.isin,
                            weight: position.weight,
                            titles: position.titles,
                            price: position.lastPriceInProductCurrency,
                            plusMinus: position.plusMinus,
                            plusMinusPercentage: position.plusMinusPercentage,
                            value: position.value,
                            instrumentType: position.instrumentType,
                            valuation: position.value,
                            buyAmount: position.value === 0 || position.value == null
                                ? 0
                                : position.value - position.plusMinus,
                            exchangeRate: position.exchangeRate.value !== undefined
                                ? position.exchangeRate.value
                                : position.exchangeRate,
                            from: position.exchangeRate.from !== undefined
                                ? position.exchangeRate.from
                                : "",
                            lastPriceDate: position.dateLastPriceAsString
                        });
                    }
                });
            }

            categories.push(
            {
                name: aggregation.data.name,
                subcategories: subcategories,
                buyAmount: aggregationBuyAmount,
                valuation: aggregationValuation,
                weight: aggregationWeight,
                plusMinus: aggregationPlusMinus,
                plusMinusPercentage: aggregationPlusMinusPercentage,
                growth: aggregationGrowth,
                positions: categoriesPosition
            });
        });

        let initialRegisters: IInitialRegisters[] =
        [
            {
                name: 'Total Cartera',
                documents: [],
                titles: '-',
                bought: !isNaN(buyAmount) ? currencyFormatter.format(buyAmount) : '-',
                valuation: !isNaN(valuation) ? currencyFormatter.format(valuation) : '-',
                revaluation: !isNaN(plusMinus) ? currencyFormatter.format(plusMinus) : '-',
                revaluationPercentage: !isNaN(growth) ? percentFormatter.format(growth) : '-',
                weight: weight ? percentFormatter.format(weight) : '-',
                pmpc: '-',
                vLiquid: '-',
                exchangeRate: '-',
                liquidDate: '-',
                ironiaPoints: '-',
                actions: '-',
                type: 'total',
                expanded: false,
                hidden: false,
                category: '-',
                currency: ''
            }
        ];

        categories.forEach((category: TCategories) =>
        {
            initialRegisters.push(
            {
                name: category.name ? category.name : '-',
                documents: [],
                titles: '-',
                bought: !isNaN(category.buyAmount) ? currencyFormatter.format(category.buyAmount) : '-',
                valuation: !isNaN(category.valuation) ? currencyFormatter.format(category.valuation) : '-',
                revaluation: !isNaN(category.plusMinus) ? currencyFormatter.format(category.plusMinus) : '-',
                revaluationPercentage: !isNaN(category.plusMinusPercentage) ? percentFormatter.format(category.plusMinusPercentage) : '-',
                weight: category.weight ? percentFormatter.format(category.weight) : '-',
                pmpc: '-',
                vLiquid: '-',
                exchangeRate: '-',
                liquidDate: '-',
                ironiaPoints: '-',
                actions: '-',
                type: 'category',
                hidden: false,
                expanded: true,
                category: 'Total',
                currency: '-',
            });

            category.positions.forEach((position: TSubCategoriesPositions) =>
            {
                if(position.instrumentType !== 21)
                {
                    initialRegisters.push(
                    {
                        name: position.name,
                        isin: position.isin,
                        documents: [],
                        titles: '-',
                        bought: position.value,
                        valuation: position.value,
                        revaluation: undefined,
                        revaluationPercentage: '-',
                        weight: position.weight ? percentFormatter.format(position.weight) : '-',
                        pmpc: '-',
                        vLiquid: position.value,
                        exchangeRate: '-',
                        liquidDate: position.lastPriceDate,
                        ironiaPoints: '-',
                        actions: '-',
                        type: 'position',
                        hidden: false,
                        category: category.name ? category.name : '',
                        score: '-',
                        instrumentId: position.instrumentId,
                        allFundsId: undefined,
                        benchmarkFinametrixId: undefined,
                        currency: position.currency
                    });
                }
                else
                {
                    // No condicionar por position.buyAmount: en datos crudos (level2 vacío) no existe ese campo,
                    // y cuando sí existe, buyAmount 0 es válido y no debe ocultar la fila.
                    const fund = props.funds?.find((f: IInfoFundPortfolio) => f.isin === position.isin);
                    const buyAmountNum = position.buyAmount != null
                        ? Number(position.buyAmount)
                        : (typeof position.value === 'number' && typeof position.plusMinus === 'number'
                            ? position.value - position.plusMinus
                            : NaN);

                    initialRegisters.push(
                    {
                        name: position.name,
                        isin: position.isin,
                        available: fund ? fund.available : true,
                        documents: fund ? fund.documents : [],
                        titles: position.titles ? longDecimalFormatter.format(position.titles) : '-',
                        bought: !isNaN(buyAmountNum) ? currencyFormatter.format(buyAmountNum) : '-',
                        valuation: position.valuation != null && !isNaN(Number(position.valuation)) ? currencyFormatter.format(Number(position.valuation)) : '-',
                        revaluation: position.plusMinus != null && !isNaN(Number(position.plusMinus)) ? currencyFormatter.format(Number(position.plusMinus)) : '-',
                        revaluationPercentage: !isNaN(position.plusMinusPercentage) ? percentFormatter.format(position.plusMinusPercentage) : '-',
                        linearReturnYtd: fund?.linearReturnYtd !== undefined ? percentFormatter.format(fund.linearReturnYtd) : '-',
                        linearReturn1d: fund?.linearReturn1d !== undefined ? percentFormatter.format(fund.linearReturn1d) : '-',
                        volatilityOneYear: fund?.volatilityOneYear !== undefined ? percentFormatter.format(fund.volatilityOneYear) : '-',
                        weight: position.weight ? percentFormatter.format(position.weight) : '-',
                        pmpc: !isNaN(buyAmountNum) && position.titles ? currencyFormatter.format(buyAmountNum / position.titles) : '-',
                        vLiquid: position.price ? setCurrency(position.from, position.price) : '-',
                        exchangeRate: position.exchangeRate != null && typeof position.exchangeRate === 'object' && 'value' in position.exchangeRate && (position.exchangeRate as { value?: number }).value !== undefined
                            ? decimalFormatter.format((position.exchangeRate as { value: number }).value)
                            : '-',
                        liquidDate: position.lastPriceDate ? dateFormat(position.lastPriceDate) : '-',
                        ironiaPoints: '-',
                        actions: '-',
                        type: 'position',
                        hidden: false,
                        category: category.name ? category.name : '',
                        score: fund ? fund.averageScore : 0,
                        finametrixId: position.instrumentId,
                        instrumentId: position.instrumentId,
                        instrumentType: position.instrumentType,
                        allFundsId: fund ? fund.allFundsId : '',
                        benchmarkFinametrixId: fund ? fund.benchmarkFinametrixId : '',
                        managementFee: fund ? fund.managementFee : undefined,
                        noMin: fund ? fund.noMin : undefined,
                        minimumInitialInvestment: fund ? fund.minimumInitialInvestment : undefined,
                        ongoingCharges: fund ? fund.ongoingCharges : undefined,
                        rebate: fund ? fund.rebate : undefined,
                        complexity: fund ? fund.complexity : false,
                        minimumAdditionalInvestment: fund ? fund.minimumAdditionalInvestment : undefined,
                        cleanShare: fund ? fund.cleanShare : false,
                        currency: position.currency
                    });
                }
            });

            category.subcategories.forEach((subcategory: TSubcategories) =>
            {
                initialRegisters.push(
                {
                    name: subcategory.name,
                    documents: [],
                    titles: '-',
                    bought: !isNaN(subcategory.buyAmount) ? currencyFormatter.format(subcategory.buyAmount) : '-',
                    valuation: !isNaN(subcategory.valuation) ? currencyFormatter.format(subcategory.valuation) : '-',
                    revaluation: !isNaN(subcategory.plusMinus) ? currencyFormatter.format(subcategory.plusMinus) : '-',
                    revaluationPercentage: !isNaN(subcategory.plusMinusPercentage) ? percentFormatter.format(subcategory.plusMinusPercentage) : '-',
                    weight: subcategory.weight ? percentFormatter.format(subcategory.weight) : '-',
                    pmpc: '-',
                    vLiquid: '-',
                    liquidDate: '-',
                    exchangeRate: '-',
                    ironiaPoints: '-',
                    actions: '-',
                    type: 'subcategory',
                    hidden: false,
                    expanded: true,
                    category: category.name ? category.name : '',
                });

                subcategory.positions.forEach((position: TSubCategoriesPositions) =>
                {
                    const fund = props.funds?.find((f: IInfoFundPortfolio) => f.isin === position.isin);
                    const exchangeRateValue = typeof position.exchangeRate === 'object' && position.exchangeRate !== null && 'value' in position.exchangeRate
                        ? (position.exchangeRate as { value?: number }).value
                        : position.exchangeRate as number;
                    const exchangeRate = exchangeRateValue !== undefined ? exchangeRateValue : (position.exchangeRate as number);
                    initialRegisters.push(
                    {
                        name: position.name,
                        isin: position.isin,
                        available: fund ? fund.available : true,
                        documents: fund ? fund.documents : [],
                        titles: position.titles ? longDecimalFormatter.format(position.titles) : '-',
                        bought: !isNaN(position.buyAmount) ? currencyFormatter.format(position.buyAmount) : '-',
                        valuation: !isNaN(position.valuation) ? currencyFormatter.format(position.valuation) : '-',
                        revaluation: !isNaN(position.plusMinus) ? currencyFormatter.format(position.plusMinus) : '-',
                        revaluationPercentage: !isNaN(position.plusMinusPercentage) ? percentFormatter.format(position.plusMinusPercentage) : '-',
                        linearReturnYtd: fund?.linearReturnYtd !== undefined ? percentFormatter.format(fund.linearReturnYtd) : '-',
                        linearReturn1d: fund?.linearReturn1d !== undefined ? percentFormatter.format(fund.linearReturn1d) : '-',
                        volatilityOneYear: fund?.volatilityOneYear !== undefined ? percentFormatter.format(fund.volatilityOneYear) : '-',
                        weight: position.weight ? percentFormatter.format(position.weight) : '-',
                        pmpc: !isNaN(position.buyAmount) && position.titles ? currencyFormatter.format(position.buyAmount / position.titles) : '-',
                        vLiquid: position.price ? setCurrency(position.from, position.price) : '-',
                        exchangeRate: exchangeRate === 1 ? (1 / exchangeRate) : decimalFormatter.format(1 / exchangeRate),
                        liquidDate: dateFormat(position.lastPriceDate),
                        ironiaPoints: '-',
                        actions: '-',
                        type: 'position',
                        hidden: false,
                        category: category.name ? category.name : '',
                        subcategory: subcategory.name,
                        score: fund ? fund.averageScore : 0,
                        finametrixId: position.instrumentId,
                        instrumentType: position.instrumentType,
                        instrumentId: position.instrumentId,
                        allFundsId: fund ? fund.allFundsId : '',
                        benchmarkFinametrixId: fund ? fund.benchmarkFinametrixId : '',
                        managementFee: fund ? fund.managementFee : undefined,
                        noMin: fund ? fund.noMin : false,
                        minimumInitialInvestment: fund ? fund.minimumInitialInvestment : undefined,
                        ongoingCharges: fund ? fund.ongoingCharges : undefined,
                        rebate: fund ? fund.rebate : undefined,
                        complexity: fund ? fund.complexity : false,
                        minimumAdditionalInvestment: fund ? fund.minimumAdditionalInvestment : undefined,
                        cleanShare: fund ? fund.cleanShare : false,
                        currency: position.currency
                    });
                });
            });
        });

        props.fundsforModelData(initialRegisters.filter(x => x.documents && x.type === "position" && x.documents.length > 0));

        // Esta salida es exclusivamente para los datos de simulación, los cuales no necesitan documentos
		props.fundsForSimulation(initialRegisters.filter(x => x.type === "position"));

        setRegisters(initialRegisters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.funds, props.reporting?.positions.positionsTo.aggregation]);

    // Solo mantener collectedItems y notificar al padre cuando hay callback (ej. Market); evita trabajo en Summary.
    useEffect(() =>
    {
        if(!onCollectedItemsUpdate)
        {
            return;
        }
        const visibleRegisters = registers.filter((register) => !register.hidden);
        setCollectedItems((prevItems) =>
        {
            const itemsToAdd = visibleRegisters.filter((item) => !prevItems.some((collectedItem) => collectedItem.name === item.name));
            if(itemsToAdd.length > 0)
            {
                return [...prevItems, ...itemsToAdd];
            }
            return prevItems;
        });
    }, [onCollectedItemsUpdate, registers]);

    useEffect(() =>
    {
        if(onCollectedItemsUpdate)
        {
            onCollectedItemsUpdate(collectedItems);
        }
    }, [collectedItems, onCollectedItemsUpdate]);

    const toggleCategory = (categoryName: string) =>
    {
        const foundItem = registers.find((item: IInitialRegisters) => item !== undefined && item.type === 'category' && item.name === categoryName);
        const hidden: boolean | undefined = foundItem ? foundItem.expanded : false;

        setRegisters(registers.map((item: IInitialRegisters) =>
        {
            switch(item.type)
            {
                case "category":
                    if(item.name === categoryName)
                    {
                        return { ...item, expanded: !item.expanded };
                    }
                    break;
                case "subcategory":
                    if(item.category === categoryName)
                    {
                        return { ...item, hidden };
                    }
                    break;
                case "position":
                    if(item.category === categoryName)
                    {
                        const subcategory = registers.find(register => register.type === 'subcategory' && register.name === item.subcategory);
                        const newHidden = subcategory ? (hidden || !subcategory.expanded) : hidden;
                        return { ...item, hidden: newHidden };
                    }
                    break;
                default:
                    break;
            }

            return item;
        }));
    };
    const toggleSubcategory = (categoryName: string) =>
    {
        const foundItem = registers.find((item: IInitialRegisters) => item !== undefined && item.type === 'subcategory' && item.name === categoryName);

        const hidden: boolean | undefined = foundItem ? foundItem.expanded : false;

        setRegisters(registers.map((currentValue: IInitialRegisters) =>
        {
            if(currentValue.type === "subcategory" && currentValue.name === categoryName)
            {
                return { ...currentValue, expanded: !currentValue.expanded };
            }
            if(currentValue.subcategory === categoryName)
            {
                return { ...currentValue, hidden };
            }
            return currentValue;
        }));

    };
    const toggleAttachmentsVisible = (isin: string) =>
    {
        const register = registers.find((item: IInitialRegisters) => item.isin === isin);
        setSelectedRegister(register);
        setIsAttachmentsVisible(true);
    };

    const portfolioCategoryAndSubcategory = (item: IInitialRegisters, kind: 'category' | 'subcategory') =>
    {
        return(
            <span className={"column-title " + item.type}>
                <PrimaryButton onClick={() => kind === 'category' ? toggleCategory(item.name ?? '') : toggleSubcategory(item.name ?? '')} className="toggleGroupVisibility">
                    <i className={"ironia-icon " + (item.expanded ? "keyboard-arrow-up" : "keyboard-arrow-down")} />
                </PrimaryButton>
                {item.name}
            </span>
        )
    }

    const renderItemColumn = (item: IInitialRegisters, index?: number, column?: IColumn) =>
    {
        if(!column)
        {
            return null;
        }
        const fieldContent = column.fieldName ? item[column.fieldName as keyof IInitialRegisters] : undefined;
        switch(column.key)
        {
            case "name":
                switch(item.type)
                {
                    case "position":
                        return(
                            <React.Fragment>
                                <Link to={`/${item.isin}`}>
                                    <span className={"column-title " + item.type}>
                                        {item.name}
                                    </span>
                                </Link>
                                <br />
                                <span className={"column-title " + item.type}>
                                    {item.isin}
                                </span>
                            </React.Fragment>
                        );
                    case "category":
                        return(portfolioCategoryAndSubcategory(item, 'category'));
                    case "subcategory":
                        return(portfolioCategoryAndSubcategory(item, 'subcategory'));
                    default:
                        return(
                            <span className={"column-title " + item.type}>
                                {item.name}
                            </span>
                        );
                }
            case "documents":
                if(item.documents === undefined || item.documents.length === 0)
                {
                    return "";
                }
                else
                {
                    return(
                        <span style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                            <TooltipHost content="Documentos" id={`buttonId${item.isin}`} styles={{root: {display: "inline-block"}}} calloutProps={{gapSpace: 0}}>
                                <PrimaryButton className="light-circled-icon document-icon" id={"buttonId" + item.isin}
                                    onClick={() => item.isin != null && toggleAttachmentsVisible(item.isin)}
                                >
                                    <i className={"ironia-icon pdf"} />
                                </PrimaryButton>
                            </TooltipHost>
                        </span>
                    );
                }
            case "ironiaPoints":
                return <IroniaPoints score={item.score} />;
            case "titles":
            case "bought":
            case "valuation":
            case "revaluation":
            case "weight":
            case "pmpc":
            case "vLiquid":
            case "exchangeRate":
            case "liquidDate":
            case "volatilityOneYear":
                return(
                    <span className={item.type + " table-numeric-column"}>
                        {fieldContent}
                    </span>
                );
            case "revaluationPercentage":
            case "linearReturnYtd":
            case "linearReturn1d":
                if(fieldContent === undefined)
                {
                    return(
                        <span className={item.type + " table-numeric-column"}>
                            -
                        </span>
                    );
                }
                const valueRtb = parseNumber(fieldContent, 2);
                return(
                    <span className={item.type + " table-numeric-column"}>
                        {fieldContent} {valueRtb > 0 && <IncrementIcon />} {valueRtb < 0 && <DecrementIcon />}
                    </span>
                );
            default:
                return <span className={item.type}>{fieldContent}</span>;
        }
    };

    const renderAttachmentColumn = (item: TDocuments, index?: number, column?: IColumn) =>
    {
        if(!column)
        {
            return null;
        }
        switch(column.key)
        {
            case 'link':
                return (<a href={item.url} target="_blank" rel="noreferrer"><i className={'ironia-icon downloads'} /> Descargar</a>)
            default: return column.key ? (item[column.key as keyof TDocuments]) : null;
        }
    }

    const fundPositions = props.reporting?.positions.positionsTo.positions?.filter((pos: TLevel2PositionRaw) => pos.isin !== null) ?? [];
    if(fundPositions?.length === 0)
    {
        return(
            <p className="description" style={{cursor: 'default'}}>
                No hay fondos en la cartera seleccionada
            </p>
        );
    }
    else
    {
        if(registers.length < 2)
        {
            return(
                <>
                    <Spinner size={SpinnerSize.large} />
                </>
            );
        }

        return(
            <React.Fragment>
                <div className={`ms-Grid-col ms-sm12`}>
                    <DetailsList items={registers.filter((register: IInitialRegisters) => !register.hidden)}
                        columns={columns}
                        selectionMode={SelectionMode.none}
                        onRenderRow={(props?: IDetailsRowProps, defaultRender?: IRenderFunction<IDetailsRowProps>) =>
                        {
                            if(!props)
                            {
                                return null;
                            }
                            return defaultRender ? defaultRender({...props, className: props.item.type}) : null;
                        }}
                        onRenderItemColumn={renderItemColumn}
                    />
                </div>
                {isAttachmentsVisible && selectedRegister?.isin &&
                (
                    <Callout gapSpace={0} target={`#buttonId${selectedRegister.isin}`} onDismiss={() => setIsAttachmentsVisible(false)} setInitialFocus isBeakVisible={false}>
                        <DefaultButton style={{ marginTop: '8px' }} onClick={() => setIsAttachmentsVisible(false)} className="closeButton">
                            <i className='ironia-icon cross' />
                        </DefaultButton>
                        <h4 style={{padding: "0px 20px 0px 20px"}}>Documentos {selectedRegister.name}</h4>
                        <DetailsList
                            items={selectedRegister.documents ?? []}
                            columns={documentsColumns}
                            onRenderItemColumn={renderAttachmentColumn}
                            selectionMode={SelectionMode.none}
                        />
                    </Callout>
                )}
            </React.Fragment>
        );
    }
};

export default PortfolioTable;